import { z } from "zod";

/**
 * Словарь голосов озвучки: кто говорит, на каком языке и как его зовут.
 *
 * Отдельным файлом от контракта (`tts.ts`), потому что графики у них разные.
 * Контракт меняется, когда меняется форма запроса или ключ кэша, то есть
 * редко и с миграцией смысла; словарь меняется, когда отобрали новый голос,
 * сняли старый или добавили язык, и это правка одной таблицы, за которой стоит
 * прослушивание, а не проектирование.
 *
 * ⚠️ Идентификатора голоса у провайдера здесь нет ни одного, и это не
 * аккуратность, а срок годности. Набор голосов ElevenLabs доступен только
 * аккаунтам старше марта 2026 и целиком перестаёт работать 31.12.2026 (раздел 7
 * docs/tts-design.md). Чужой идентификатор, попавший в сохранённую задачу,
 * к этой дате станет мёртвой строкой, а наш переживёт и смену набора, и смену
 * провайдера. Отображение «наш id в voice_id» живёт одной таблицей рядом с
 * адаптером в `apps/api` и наружу не уезжает.
 */

/**
 * Язык явным значением с первого дня, хотя значение сейчас одно: немецкий
 * приедет значением, а не миграцией.
 *
 * ⚠️ Язык есть и у задачи, и у ГОЛОСА, и сервер их сверяет
 * (`mismatchedTtsVoice`). Раньше язык был свойством одной задачи, а голос его
 * не нёс, то есть немецкий текст можно было заказать английским голосом, и
 * возразить было некому.
 */
export const TtsLanguage = z.enum(["en"]);
export type TtsLanguage = z.infer<typeof TtsLanguage>;

/**
 * Идентификаторы голосов. Отобраны ушами 05.09.2026 из тринадцати кандидатов
 * (docs/tts-voice-audition.md, раздел 7).
 *
 * ⚠️ Голос отсюда НЕ удаляется никогда, он помечается снятым (`retired`).
 * Задачи, озвученные им, лежат в базе целиком, вместе с голосом в jsonb, и
 * удалённый идентификатор превратил бы их разбор в ошибку схемы. Снятый голос
 * не предлагается в выборе и отвечает внятным отказом на попытку повторить
 * старую задачу, а MP3, который им уже сделан, остаётся рабочим: он наш.
 */
export const TTS_VOICE_IDS = [
  "en-gb-female-confident",
  "en-gb-female-young",
  "en-gb-female-editorial",
  "en-gb-female-character",
  "en-gb-male-formal",
  "en-gb-male-warm",
  "en-gb-male-calm",
  "en-us-female-young",
  "en-us-male-formal",
  "en-us-male-senior",
] as const;

export const TtsVoiceId = z.enum(TTS_VOICE_IDS);
export type TtsVoiceId = z.infer<typeof TtsVoiceId>;

export interface TtsVoiceSpec {
  /**
   * Ярлык голоса: то, чем его зовут между собой. Стоит в выборе перед
   * описанием («Hugh · Британский мужской, строгий»), потому что описание
   * объясняет, а имя запоминается: собрав диалог, преподаватель держит в голове
   * «Hugh и Daisy», а не «строгого и молодую».
   *
   * ⚠️ **Это НАШЕ поле, скопированное у провайдера один раз, а не запрос в его
   * каталог** (раздел 7 docs/tts-design.md). Набор голосов у него меняется
   * поколениями, подписи из API однажды приедут пустыми, а привязка к живому
   * каталогу означала бы поход наружу ради строки в выпадающем списке. По той
   * же причине, по какой здесь нет ни одного `voice_id` (шапка файла).
   *
   * ⚠️ Двум ролям одного актёра нужны РАЗЛИЧИМЫЕ имена: две одинаковые строки
   * в списке хуже, чем никаких.
   */
  name: string;
  /**
   * Язык голоса. Сервер сверяет его с языком задачи и отклоняет заказ, где они
   * разошлись, а выбор группирует голоса по нему.
   */
  language: TtsLanguage;
  /**
   * Пол голоса. Заведён 07.09.2026 ради вопросов к аудированию (раздел 6.2
   * docs/listening-questions-design.md).
   *
   * ⚠️ **Поле, а не разбор идентификатора.** Слово «female» действительно стоит
   * посреди всех сегодняшних слагов, и соблазн вынуть его оттуда стоил бы одну
   * строку. Но слаг это ИМЯ, а не запись о голосе: его формат нигде не объявлен
   * и ничем не стережётся, первый же голос, названный иначе, дал бы разбору
   * молчаливый промах, а промах здесь не косметический. По этому полю строится
   * метка говорящего («the woman»), которой ученик РАЗЛИЧАЕТ собеседников на
   * слух: ошибись в нём, и мы задаём вопрос про женщину о том, что сказал
   * мужчина.
   *
   * ⚠️ Это НЕ то же самое, что пол персонажа: поле описывает, как звучит запись,
   * а не кого играет голос. Различие важно ровно там, где оно используется:
   * метку получает тот, кого слышно.
   *
   * ⚠️ Подписи (`label`) для этого не годятся вовсе: они по-русски и написаны
   * для выпадающего списка, то есть меняются вместе со вкусом, а не со звуком.
   */
  gender: "female" | "male";
  /**
   * Подпись в выборе голоса. Называет то, по чему голос выбирают: вариант
   * языка, пол, характер звучания. Имена провайдера («Storyteller Oxley
   * Grandpa») ОПИСАНИЕМ работать не могут, а половина из них ещё и вводит в
   * заблуждение; ярлыком то же имя работает лучше описания, отсюда два поля.
   */
  label: string;
  /** Что нужно знать до нажатия. `null` там, где знать нечего. */
  note: string | null;
  /**
   * Тот же актёр в другой роли. В одном диалоге такую пару не ставят: голоса
   * сольются, а ученик собеседников не видит и различает их только на слух.
   */
  sameSpeakerAs?: TtsVoiceId;
  /** Снят с выбора. Смотри предупреждение у `TTS_VOICE_IDS`. */
  retired?: boolean;
}

/**
 * Имена взяты у провайдера один раз, из таблицы отбора
 * (`docs/tts-voice-audition.md`, раздел 7, колонка «Имя у провайдера»), и
 * укорочены до произносимого: «Olivia J» и «Storyteller Oxley - Grandpa» это
 * подписи каталога, а не то, чем голос зовут вслух.
 *
 * ⚠️ Двум ролям одного актёра (оба Oxley) имена разведены: «Oxley» и «Oxley
 * Sr.». Одинаковые строки в списке хуже, чем никаких, а старший из двух это
 * как раз пожилой рассказчик.
 */
export const TTS_VOICES: Readonly<Record<TtsVoiceId, TtsVoiceSpec>> = {
  "en-gb-female-confident": {
    name: "Olivia",
    language: "en",
    gender: "female",
    label: "Британский женский, уверенный",
    note: null,
  },
  "en-gb-female-young": {
    name: "Daisy",
    language: "en",
    gender: "female",
    label: "Британский женский, молодой",
    note: null,
  },
  "en-gb-female-editorial": {
    name: "Amanda",
    language: "en",
    gender: "female",
    label: "Британский женский, дикторский",
    note: null,
  },
  "en-gb-female-character": {
    name: "Lizzie",
    language: "en",
    gender: "female",
    label: "Британский женский, кокни",
    note: "Сильный лондонский акцент: для экзаменационного текста не годится, а на роль в диалоге подойдёт",
  },
  "en-gb-male-formal": {
    name: "Hugh",
    language: "en",
    gender: "male",
    label: "Британский мужской, строгий",
    note: null,
  },
  "en-gb-male-warm": {
    name: "Tom",
    language: "en",
    gender: "male",
    label: "Британский мужской, тёплый",
    note: null,
  },
  "en-gb-male-calm": {
    name: "Jimmy",
    language: "en",
    gender: "male",
    label: "Британский мужской, спокойный",
    note: null,
  },
  "en-us-female-young": {
    name: "Belle",
    language: "en",
    gender: "female",
    label: "Американский женский, молодой",
    note: null,
  },
  "en-us-male-formal": {
    name: "Oxley",
    language: "en",
    gender: "male",
    label: "Американский мужской, строгий",
    note: null,
    sameSpeakerAs: "en-us-male-senior",
  },
  "en-us-male-senior": {
    name: "Oxley Sr.",
    language: "en",
    gender: "male",
    label: "Американский мужской, пожилой",
    note: null,
    sameSpeakerAs: "en-us-male-formal",
  },
};

/**
 * Что показывать в выборе голоса. Снятые не предлагаются.
 *
 * Словарь параметром ради теста: снятых голосов сегодня нет ни одного, и на
 * настоящем словаре фильтр не выполняется ни разу, то есть проверить его можно
 * только на подставном.
 */
export function activeTtsVoices(
  voices: Readonly<Record<TtsVoiceId, TtsVoiceSpec>> = TTS_VOICES,
): { id: TtsVoiceId; spec: TtsVoiceSpec }[] {
  return TTS_VOICE_IDS.filter((id) => voices[id].retired !== true).map((id) => ({
    id,
    spec: voices[id],
  }));
}

/**
 * ⚠️ Проверка отдельной функцией, а не условием в схеме, и это осознанно.
 * Схема разбирает и живой запрос, и `request` из jsonb старой задачи; запретив
 * снятый голос в схеме, мы сделали бы неразбираемой историю, ради сохранности
 * которой голос и не удаляется. Отказывает ручка на создании задачи.
 */
export function isRetiredTtsVoice(id: TtsVoiceId): boolean {
  return TTS_VOICES[id].retired === true;
}

/**
 * Первая реплика, чей голос говорит не на языке задачи. `null` означает «все
 * сходятся».
 *
 * ⚠️ Проверка отдельной функцией, а не условием в схеме, по той же причине, что
 * у снятого голоса: схема разбирает и живой запрос, и `request` из jsonb старой
 * задачи. Отказывает ручка на создании.
 *
 * ⚠️ Словарь и язык параметрами ради теста, и здесь это не формальность:
 * язык у голосов сегодня один, то есть на настоящем словаре условие не
 * выполняется ни разу, и проверить его можно только на подставном. Иначе тест
 * зеленел бы и с выкинутой проверкой.
 */
export function mismatchedTtsVoice(
  lines: readonly { voice: TtsVoiceId }[],
  language: string,
  voices: Readonly<Record<TtsVoiceId, { language: string }>> = TTS_VOICES,
): TtsVoiceId | null {
  return lines.find((line) => voices[line.voice].language !== language)?.voice ?? null;
}
